/**
 * Turn a TanStack Router routeId into a clean Sentry transaction name.
 *
 * The whole authenticated app is nested under the `/_protected` pathless
 * layout route (see `src/routes/_protected.tsx`), and consumer-facing routes
 * are further nested under the `/_consumer` pathless layout (see
 * `src/routes/_protected/_consumer.tsx`). Neither adds a URL segment, but both
 * show up in the routeId the `tanstackRouterBrowserTracingIntegration` matches
 * (e.g. `/_protected/_consumer/wallet/index`). Those prefixes are internal
 * routing structure — Sentry's `transaction` field should describe the
 * user-facing route, not the router's layout bookkeeping, so we strip them.
 *
 * Dynamic segments (`$userId`, `$receiptId`, …) are kept verbatim so Sentry
 * groups by route rather than splintering per concrete id (source = "route").
 *
 * Errors captured before a leaf route commits land on the bare `/_protected`
 * layout routeId. This happens on the auth/bootstrap/redirect funnel:
 * `_protected.beforeLoad` throws a WorkOS `redirect`, intentionally hangs via
 * `await new Promise<never>(() => {})` while the redirect is in flight, or an
 * unmatched child falls through to the layout + `defaultNotFoundComponent`. In
 * those cases the layout is the terminal route match, so we fall back to the
 * request pathname to point the issue at the real page the user hit.
 *
 * @param transaction  - The raw transaction/routeId from the Sentry event.
 * @param fallbackPathname - The request pathname, used when `transaction` is
 *   only the bare `/_protected` layout route (i.e. no leaf resolved).
 */
export function normalizeTransactionName(
  transaction: string | undefined,
  fallbackPathname?: string,
): string | undefined {
  if (!transaction) return undefined;

  // Strip the leading `/_protected` (then nested `/_consumer`) pathless layout
  // prefixes only. Anchored to the start so a real `_protected`/`_consumer` path
  // segment deeper in the route is left untouched.
  const stripped = transaction.replace(/^\/?_protected/, '').replace(/^\/_consumer/, '');

  const isLayoutOnly = stripped === '' || stripped === '/';
  if (isLayoutOnly) {
    if (fallbackPathname && fallbackPathname !== '/') {
      return fallbackPathname.replace(/\/+$/, '') || '/';
    }
    return '/';
  }

  // `/index` is the routeId TanStack Router assigns to a directory's index
  // route — it maps to the directory root, so drop a trailing `/index`.
  const withoutIndex = stripped.replace(/\/index$/, '');
  if (withoutIndex === '') return '/';

  // Ensure a leading slash, drop any trailing slash, keep $param segments.
  const withSlash = withoutIndex.startsWith('/') ? withoutIndex : `/${withoutIndex}`;
  return withSlash.replace(/\/+$/, '') || '/';
}
